// Popup explicativo que se muestra la PRIMERA vez que el usuario toca "Editar
// con estudio" en el detalle de un video del historial (solo desktop): explica
// qué hace el botón y a dónde lo lleva, antes de crear el proyecto. Se marca
// visto por cuenta (onboarding_seen.estudio_from_historial), así aparece una
// sola vez.
//
// Mismo lenguaje visual y a11y que FeatureLockedModal / DesktopOnlyModal
// (portaleado a body, tarjeta centrada, ícono en círculo, role=dialog, Escape),
// pero con dos acciones: Cancelar o confirmar (crea el proyecto + entra).

import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { Clapperboard } from 'lucide-react';

export interface StudioIntroModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const StudioIntroModal: React.FC<StudioIntroModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const titleId = useId();
  const descId = useId();

  // Cerrar con Escape — comportamiento estándar de un diálogo modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="bg-surface-card rounded-lg shadow-md w-full max-w-md mx-4"
      >
        <div className="p-5">
          <div className="flex justify-center mb-4">
            <span className="w-12 h-12 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
              <Clapperboard className="w-6 h-6 text-brand-primary" />
            </span>
          </div>
          <h3
            id={titleId}
            className="text-[17px] font-semibold text-brand-black/90 mb-2 text-center"
          >
            Editar en el Estudio de Video
          </h3>
          <p id={descId} className="text-[15px] text-neutral-600 mb-6 text-center">
            Vamos a crear un proyecto en el <strong>Estudio de Video</strong> con
            este video ya puesto en la timeline y te llevamos al editor completo
            para trabajarlo (cortes, música, textos, transiciones y más). Es una
            herramienta de escritorio.
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
            >
              Ir al Estudio
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default StudioIntroModal;
